import React, { forwardRef, useCallback, useContext, useMemo } from 'react'
import { MuiThemeProvider, TablePagination } from '@material-ui/core'

import Table from './Table'
import { Container } from 'react-bootstrap'
import TablePanel from './render/TablePanel'
import objectPath from 'object-path'
import Title from './render/Title'
import { getCheckValue } from 'designer/component/action/Data'
import { DesignContext } from 'contexts/DesignContext'
import { checkTableCondition } from './Render'
import { isFilled } from 'designer/component/action/condition/State'
import TableOverrideStyle from './TableOverrideStyle'

const ApiTable = ({
  title,
  editable,
  tableAction,
  tableColumn,
  dataTable,
  tableRef,
  uischema,
  formData,
  setModalAction,
  renderTableData,
  tableColumnList,
  loading,
  detailColumns,
  rowsPerPageOptions,
  page,
  rowsPerPage,
  handleChangePage,
  handleChangeRowsPerPage,
  totalCount,
  getAction,
  addActionRef,
  dataRowClicked,
  isPagination,
  allParams,
  isClickButton,
  setIsClickButton,
  setLoading,
  isRenderTable,
  setIsRenderTable,
  fieldKey,
  fieldName,
  renderSummaryRow,
  tableTheme,
  propAction,
  localization,
}) => {
  const designContext = useContext(DesignContext)
  const isSelectRowOnTab =
    objectPath.get(uischema, 'action.name') === 'selectRow' &&
    objectPath.get(uischema, 'action.selectType') === 'onTab'
  const onRowClickProp =
    objectPath.get(uischema, 'action.event') === 'onRowClick' ||
    isSelectRowOnTab
      ? {
          onRowClick: (event, rowData) => {
            if (isClickButton) {
              setIsClickButton(false)
            } else {
              getAction(rowData)
            }
          },
        }
      : null

  const isRenderComponent =
    objectPath.get(uischema, 'action.renderComponent') ||
    objectPath.get(uischema, 'action.linkType') === 'samePath'

  const refColumnId = objectPath.get(uischema, 'action.refColumnId')
  const rowSelectId = isRenderComponent
    ? getCheckValue(objectPath.get(allParams, refColumnId))
    : undefined
  const conditionFormData = designContext?.jsonForm?.formData
  const conditionDatasourceValue = objectPath.get(designContext, '@datasource')
  const conditionContext = useMemo(
    () => ({
      jsonForm: {
        formData: conditionFormData,
      },
      '@datasource': conditionDatasourceValue,
    }),
    [conditionFormData, conditionDatasourceValue],
  )
  const hasDetailPanel = tableColumnList.some((column) => {
    const type = column?.typeField || column?.type
    if (type === 'detailPanel') return isFilled(column?.detailColumn)
    if (type === 'html') {
      return objectPath.get(column, 'html.formatType') === 'detail'
    }
    return false
  })

  const rowStyle = useCallback((rowData) => {
    let found = null
    tableColumnList.forEach((column) => {
      if (column.conditions) {
        let rowValue = objectPath.get(rowData, column.field)
        found = checkTableCondition(conditionContext, column, rowValue)
      }
    })

    if (rowData?.tableData?.id === dataRowClicked?.tableData?.id) {
      return {
        backgroundColor: uischema.action?.selectColor || '#dddddd',
        color: uischema.action?.selectTextColor || '#fff',
      }
    }

    if (isRenderComponent) {
      let rowId = objectPath.get(rowData, refColumnId)
      rowId = getCheckValue(rowId)
      if (rowSelectId === rowId) {
        return {
          backgroundColor: uischema.action?.selectColor || '#dddddd',
          color: uischema.action?.selectTextColor || '#fff',
        }
      }
    }

    if (found) {
      return {
        backgroundColor: found.rowColor,
        color: found.rowTextColor,
      }
    }
    return {}
  }, [
    conditionContext,
    dataRowClicked?.tableData?.id,
    isRenderComponent,
    refColumnId,
    rowSelectId,
    tableColumnList,
    uischema.action?.selectColor,
    uischema.action?.selectTextColor,
  ])

  const tableOptions = useMemo(
    () => ({
      paging: uischema.isPaging === undefined ? true : uischema.isPaging,
      idSynonym: 'id',
      addRowPosition: 'first',
      showDetailPanelIcon: hasDetailPanel,
      search: uischema?.tableAction?.searchable,
      pageSize: rowsPerPage || uischema?.rowsPerPage || 5,
      pageSizeOptions: rowsPerPageOptions,
      sorting: true,
      rowStyle,
    }),
    [
      hasDetailPanel,
      rowStyle,
      rowsPerPage,
      rowsPerPageOptions,
      uischema.isPaging,
      uischema?.rowsPerPage,
      uischema?.tableAction?.searchable,
    ],
  )

  return (
    <MuiThemeProvider theme={tableTheme}>
      <TableOverrideStyle
        title={title}
        fieldKey={fieldKey}
        uischema={uischema}
      />
      <div
        id={fieldKey}
        style={
          uischema.isPaging ? {} : { display: 'inline-grid', width: '100%' }
        }
      >
        <Table
          id={fieldKey}
          name={fieldName}
          tableIcons={{
            Add: forwardRef((props, ref) => <div ref={addActionRef}></div>),
          }}
          isLoading={loading}
          title={<Title title={title} />}
          editable={editable}
          actions={tableAction}
          tableRef={tableRef}
          columns={tableColumn}
          data={dataTable}
          localization={localization}
          totalCount={totalCount}
          selectRowOnTabAction={
            isSelectRowOnTab
              ? { getAction, isClickButton, setIsClickButton }
              : undefined
          }
          detailPanel={
            hasDetailPanel
              ? ({ rowData }) => {
                  return (
                    <Container
                      className="m-0 ml-4"
                      style={{ maxWidth: 'max-content' }}
                    >
                      <TablePanel
                        rowData={rowData}
                        tableColumn={tableColumnList}
                        detailColumns={detailColumns}
                        uischema={uischema}
                        formData={formData}
                        setModalAction={setModalAction}
                        renderTableData={renderTableData}
                        isArrayTable={true}
                        propAction={propAction}
                      />
                    </Container>
                  )
                }
              : undefined
          }
          {...onRowClickProp}
          page={page}
          options={tableOptions}
          onPageChange={handleChangePage}
          components={
            isPagination && {
              Pagination: (props) => (
                <TablePagination
                  {...props}
                  rowsPerPageOptions={rowsPerPageOptions}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  count={totalCount}
                />
              ),
            }
          }
          renderSummaryRow={renderSummaryRow}
        />
      </div>
    </MuiThemeProvider>
  )
}

export default ApiTable
